"use client";
import { Card, Collapse, Skeleton, Typography } from "antd";
import CardChevron from "@/Components/Common/CardChevron";
import "./FaqSection.css";
import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { IntlShape, useIntl } from "react-intl";
import { useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import { IFaqCategory } from "../../Orchestrator/Types/Interface/INeedHelp";
import FaqIsHelpfulSection from "./FaqIsHelpfulSection";
import { _eventBus } from "../../core/events/EventBus";
import { evtTree } from "../../Orchestrator/ConstantTree";
import { BlocksRenderer } from "../Common/CMS/ReactBlockRender";

const FaqSection = () => {
  const [prevLocale, stePreviousLocale] = useState<IntlShape["locale"]>("en");
  const intl = useIntl();
  const [faqs] = useRecState<IFaqCategory[]>("selectFaqAtom");
  const [selectFaqLoading] = useRecState<boolean>("selectFaqLoading");

  useEffect(() => {
    if (intl) {
      // return default for french
      if (
        (prevLocale === "en" && intl.locale === "fr") ||
        (prevLocale === "fr" && intl.locale === "en")
      )
        return stePreviousLocale(intl.locale);
      setTimeout(() => {
        _eventBus.emit({
          eventName: evtTree.NeedHelp.Faq.Get,
          data: {
            intl,
          },
        });
      }, 400);
      stePreviousLocale(intl.locale);
    }
  }, [intl]);

  const processedFaq = useMemo(() => {
    return faqs
      .map((faq) => {
        if (faq?.faqs.length) return faq;
        return null;
      })
      .filter((i) => i);
  }, [faqs]);

  return (
    <div className="faq-container">
      <Typography.Title level={1} className="fw-700 fs-24">
        {intl.formatMessage({ id: "faqs" })}
      </Typography.Title>

      <Card className="card-shadow mt-4 faq-card">
        <div className="d-flex flex-col gap-8">
          {selectFaqLoading ? (
            <>
              {[...Array(4)].map((_, index) => (
                <Skeleton.Button
                  key={index}
                  active={true}
                  className="faq-skeleton"
                />
              ))}
            </>
          ) : !processedFaq?.length ? (
            <div className="d-flex flex-col align-items-center justify-content-center gap-8">
              <Typography.Title level={5} className="fw-600 m-0">
                {intl.formatMessage({ id: "noFaqAvailable" })}
              </Typography.Title>
            </div>
          ) : (
            processedFaq?.map((faq, index) => (
              <Collapse
                key={index}
                className="faq-collapse"
                expandIconPlacement="end"
                expandIcon={({ isActive }) => (
                  <span>
                    <Image
                      src={
                        isActive
                          ? "/assets/need_help/red_minus.svg"
                          : "/assets/need_help/red_plus.svg"
                      }
                      alt="down_arrow"
                      width={15.5}
                      height={15.5}
                      style={{
                        transition: "transform 0.3s",
                      }}
                    />
                  </span>
                )}
                items={[
                  {
                    key: faq?.id || `faq-${index}`,
                    label: (
                      <Typography.Title level={5} className="fw-700 m-0">
                        {faq?.title}
                      </Typography.Title>
                    ),
                    children: (
                      <div>
                        <Collapse
                          key={index}
                          bordered={false}
                          className="sub-collapse"
                          expandIconPlacement="end"
                          expandIcon={({ isActive }) => (
                            <span>
                              <CardChevron
                                direction={isActive ? "up" : "down"}
                                width={9.5}
                                height={5.5}
                              />
                            </span>
                          )}
                          items={faq?.faqs?.map((faq) => ({
                            key: faq.id,
                            label: (
                              <Typography.Title
                                level={5}
                                className="fw-500 m-0"
                              >
                                {faq.question}
                              </Typography.Title>
                            ),
                            children: (
                              <div className="fw-400">
                                <BlocksRenderer content={faq?.answer || []} />
                                <div>
                                  <FaqIsHelpfulSection />
                                </div>
                              </div>
                            ),
                          }))}
                        />
                      </div>
                    ),
                  },
                ]}
              />
            ))
          )}
        </div>
      </Card>
    </div>
  );
};

export default FaqSection;
